import React, { useEffect, useRef, useState } from 'react';
import vdlPrefix from '../../util/vdlPrefix';
import classNames from 'classnames';
import { notify } from '@synerg/vdl-react-components/src/util/widget-helpers';

export type ITabOrientation = 'horizontal' | 'vertical';

export interface ITabProps {
  children?: React.ReactNode;
  className?: string;
  dataItem?: any;
  disabled?: boolean;
  focused?: boolean;
  index: number;
  orientation?: ITabOrientation;
  role?: string;
  selected?: boolean;
  onNavigate?: (direction: string) => void;
  onSelect?: (dataItem: any) => void;
}

export const Tab = ({
  children,
  className,
  dataItem,
  disabled,
  focused,
  index = 0,
  orientation = 'horizontal',
  role = 'tab',
  selected,
  onNavigate,
  onSelect,
  ...otherProps
}: ITabProps) => {
  const tabClasses = {
    [vdlPrefix('tab')]: true,
    [vdlPrefix('tab--focus')]: focused,
    [vdlPrefix('tab--selected')]: selected,
    [vdlPrefix('tab--disabled')]: disabled,
    [vdlPrefix('tab--' + orientation)]: true
  };
  const el = useRef<HTMLLIElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted && selected) {
      el.current?.focus();
    }
  }, [selected]);

  const handleOnSelect = () => {
    if (!disabled) {
      notify(onSelect, dataItem);
    }
  };

  const handleArrowKeys = (evt: any) => {
    if (evt.keyCode === 37) {
      onNavigate?.('previous');
    }
    else if (evt.keyCode === 39) {
      onNavigate?.('next');
    }
  };

  return (
    <li
      ref={el}
      {...otherProps}
      role={role}
      tabIndex={(disabled || !selected) ? -1 : 0}
      data-disabled={disabled}
      aria-selected={!!selected}
      onKeyDown={handleArrowKeys}
      className={classNames(className, tabClasses)}
      onClick={handleOnSelect}
    >
      <div className={vdlPrefix('tab__content')}>
        {children}
      </div>
    </li>
  );
};

Tab.displayName = 'Tab';
